import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Linkedin, Twitter, Instagram, ArrowUpRight } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/content";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. We'll be in touch.");
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.message("You're already subscribed.");
      } else {
        toast.error("Couldn't subscribe right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#FAF7F2] border-t border-stone-200 pt-24 pb-10 overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[300px] glow-amber pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-5">
            <img
              src="/brand/ithr-logo.png"
              alt="ITHR Technologies"
              className="h-16 md:h-20 w-auto mb-8 select-none"
              draggable="false"
            />
            <div className="font-display text-3xl md:text-4xl text-stone-900 mb-4 tracking-tighter">
              Humanizing<br />
              <span className="text-[#B8860B]">Technology.</span>
            </div>
            <p className="text-stone-600 text-base max-w-md mb-8 leading-relaxed">
              ITHR Technologies partners with ambitious organizations to build, operate and scale technology that respects the humans behind it.
            </p>

            <form onSubmit={subscribe} className="flex border border-stone-300 max-w-md">
              <input
                data-testid="footer-newsletter-email"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent text-stone-900 px-4 py-3 outline-none placeholder:text-stone-400"
              />
              <button
                data-testid="footer-newsletter-submit"
                disabled={loading}
                className="bg-[#B8860B] text-white px-5 font-medium hover:bg-stone-900 transition-colors disabled:opacity-60"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] font-semibold mb-5">
              Company
            </div>
            <ul className="space-y-3 text-stone-700">
              <li><Link className="hover:text-stone-900" to="/about" data-testid="footer-link-about">About</Link></li>
              <li><Link className="hover:text-stone-900" to="/careers" data-testid="footer-link-careers">Careers</Link></li>
              <li><Link className="hover:text-stone-900" to="/contact" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] font-semibold mb-5">
              Services
            </div>
            <ul className="space-y-3 text-stone-700">
              {SERVICES.slice(0, 5).map((s) => (
                <li key={s.id}>
                  <Link className="hover:text-stone-900" to={`/services#${s.id}`} data-testid={`footer-service-${s.id}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-[#B8860B] font-semibold mb-5">
              Headquarters
            </div>
            <address className="not-italic text-stone-700 space-y-3 text-sm">
              <div className="flex gap-3"><MapPin className="w-4 h-4 mt-1 text-[#B8860B]" /> <span>{COMPANY.address.line1}<br />{COMPANY.address.line2}<br />{COMPANY.address.line3}<br />{COMPANY.address.city}, {COMPANY.address.country}</span></div>
              <div className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#B8860B]" /> <a href={`tel:${COMPANY.phone}`} className="hover:text-stone-900">{COMPANY.phone}</a></div>
              <div className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-[#B8860B]" /> <a href={`mailto:${COMPANY.email}`} className="hover:text-stone-900">{COMPANY.email}</a></div>
            </address>
          </div>
        </div>

        <div className="border-t border-stone-200 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="text-stone-500 text-sm">
            © {new Date().getFullYear()} {COMPANY.legal}. All rights reserved.
          </div>
          <div className="flex items-center gap-4">
            <a aria-label="LinkedIn" data-testid="footer-social-linkedin" href={COMPANY.social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 border border-stone-300 flex items-center justify-center text-stone-600 hover:text-[#B8860B] hover:border-[#D4AF37] transition-colors">
              <Linkedin className="w-4 h-4" />
            </a>
            <a aria-label="Twitter" data-testid="footer-social-twitter" href={COMPANY.social.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 border border-stone-300 flex items-center justify-center text-stone-600 hover:text-[#B8860B] hover:border-[#D4AF37] transition-colors">
              <Twitter className="w-4 h-4" />
            </a>
            <a aria-label="Instagram" data-testid="footer-social-instagram" href={COMPANY.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 border border-stone-300 flex items-center justify-center text-stone-600 hover:text-[#B8860B] hover:border-[#D4AF37] transition-colors">
              <Instagram className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
